import React, {
  useContext,
  useEffect,
  useReducer,
  useState,
  useCallback
} from 'react';
import useSkipUpdate from '../../../../hooks/useSkipRender';
import fetchAPI from '../../../../utils/fetchAPI';
import SerializationCopy from '../../../../utils/SerializationCopy';
import { AutomationsContex } from '../../../engine/FormBuilderAutomationsEngine';
import { AutomationActionsContext } from '../../engine/AutomationsActionsEngine';
import ChangeStatusView from '../view/ChangeStatusView';

const ChangeStatusReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    case 'update_statusData':
      return { ...state, statusData: { ...state.statusData, ...payload } };

    default:
      return state;
  }
};

const EmailRecipientsReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const DEFAULT_TEMPLATE_ID = '00000000-0000-0000-0000-000000000019';

const initialReducerState = {
  statusData: { status: '' }
};

const emailRecipientsInitialState = {
  typesOfRecipient: [],
  specificUsers: [],
  to: []
};

const ChangeStatusEngine = ({ update = d => d, variant, ...props }) => {
  const { statuses } = useContext(AutomationsContex);
  const { mailTemplates } = useContext(AutomationActionsContext);

  const [emailDialogData, emailDialogDispatch] = useReducer(
    EmailRecipientsReducer,
    emailRecipientsInitialState
  );
  const [data, dispatch] = useReducer(ChangeStatusReducer, initialReducerState);
  const { shouldSkipUpdate, skipUpdate } = useSkipUpdate();
  const [typesOfRecipient, setTypesOfRecipient] = useState([]);
  const [specificUsers, setSpecificUsers] = useState([]);
  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [showEditEmailBtn, setShowEditEmailBtn] = useState(false);

  useEffect(() => {
    if (shouldSkipUpdate()) {
      return;
    }
    update({ additionalParameters: { ...data } });
  }, [data]);

  useEffect(() => {
    const initReducerState = () => {
      const statusData =
        props.additionalParameters && props.additionalParameters.statusData;
      if (!statusData) {
        return;
      }

      skipUpdate();
      dispatch({ type: 'update', payload: { ...props.additionalParameters } });

      if (statusData.emailTemplateId) {
        setShowEditEmailBtn(true);
      }

      const to = statusData.to;
      if (!statusData.to) {
        return;
      }

      const typesOfRecipient = to.map(t => t.type);
      setTypesOfRecipient(typesOfRecipient);

      const usersBlockIndex = to.findIndex(t => t.type === 'specificUser');
      const isBlockExists = usersBlockIndex >= 0;

      if (isBlockExists) {
        fetchAPI('/users/v1/bulkfetch/search', {
          method: 'POST',
          body: {
            queryDescriptor: { elements: to[usersBlockIndex].userIds }
          }
        }).then(({ results }) => {
          const specificUsers = results.map(u => ({
            label: `${u.info.firstName} ${u.info.lastName} <${u.info.email}>`,
            value: u.id
          }));

          setSpecificUsers(specificUsers);
        });
      }
    };
    initReducerState();
  }, []);

  const onSendEmailClicked = useCallback(() => {
    setShowEditEmailBtn(true);
    setIsEmailDialogOpen(true);
    emailDialogDispatch({
      type: 'update',
      payload: {
        emailTemplateId:
          ((mailTemplates || []).find(t => t.code === 'automation') || {}).id ||
          DEFAULT_TEMPLATE_ID,
        to: [{ type: 'specificUser' }],
        typesOfRecipient: ['specificUser'],
        specificUsers: []
      }
    });
    setTypesOfRecipient(['specificUser']);
    setSpecificUsers([]);
  }, [mailTemplates]);

  const onTypesOfRecipientChange = useCallback(
    types => {
      const to = (types || [])
        .map(t => {
          if (t === 'assignedUser') {
            return { type: 'assignedUser' };
          }
          if (
            t === 'specificUser' &&
            (emailDialogData.specificUsers || []).length
          ) {
            return {
              type: 'specificUser',
              userIds: emailDialogData.specificUsers.map(u => u.value)
            };
          }
          return null;
        })
        .filter(t => t);
      emailDialogDispatch({
        type: 'update',
        payload: { to, typesOfRecipient: types }
      });
    },
    [emailDialogData]
  );

  const onUsersSelectChange = useCallback(
    v => {
      const to = SerializationCopy(emailDialogData.to || []);
      const usersBlockIndex = to.findIndex(t => t.type === 'specificUser');

      const isBlockExists = usersBlockIndex >= 0;
      const isValueExists = v && !!v.length;
      const plainList = isValueExists ? v.map(u => u.value) : []; // list in a format we send to the b/e (just list of ids)

      if (isBlockExists) {
        // we have block with data regarding the specific users in "to"
        if (isValueExists) {
          // current value is not empty
          // update block with current value
          to[usersBlockIndex] = { type: 'specificUser', userIds: plainList };
        } else {
          // remove the block
          to.splice(usersBlockIndex, 1);
        }
      } else {
        if (isValueExists) {
          // create the block with current users value
          to.push({ type: 'specificUser', userIds: plainList });
        }
        // else we do nothing - block doesn't exist and we don't have users ¯\_(ツ)_/¯
      }
      emailDialogDispatch({
        type: 'update',
        payload: { to, specificUsers: v }
      });
    },
    [emailDialogData]
  );

  const onOpenEmailDialogClicked = useCallback(() => {
    emailDialogDispatch({
      type: 'update',
      payload: {
        typesOfRecipient,
        specificUsers,
        to: data.statusData.to || [],
        emailTemplateId: data.statusData.emailTemplateId
      }
    });
  }, [typesOfRecipient, specificUsers, data]);

  const onTemplateSaved = useCallback(
    tId => {
      dispatch({
        type: 'update_statusData',
        payload: { emailTemplateId: tId, to: emailDialogData.to }
      });
      setTypesOfRecipient(emailDialogData.typesOfRecipient);
      setSpecificUsers(emailDialogData.specificUsers);
    },
    [emailDialogData]
  );

  const onEmailDialogCancel = useCallback(() => {
    // if the button didn't show before we opned the dialog - hide it again
    if (data.statusData.emailTemplateId) {
      return;
    }
    setShowEditEmailBtn(false);
  }, [data]);

  const viewProps = {
    variant,
    statusValue: data.statusData.status,
    options: statuses.map(s => ({
      value: s.internalId,
      text: s.label,
      'data-testid': `select-status-${s.label}`
    })),
    update,
    templateId: emailDialogData.emailTemplateId,
    setTemplateId: onTemplateSaved,
    noEmail: props.noEmail,
    onSendEmailClicked,
    specificUsers: emailDialogData.specificUsers,
    typesOfRecipient: emailDialogData.typesOfRecipient,
    onTypesOfRecipientChange,
    showUserSelectionDD:
      emailDialogData.typesOfRecipient.includes('specificUser'),
    onUsersSelectChange,
    onOpenEmailDialogClicked,
    dispatch,
    setIsEmailDialogOpen,
    isEmailDialogOpen,
    showEditEmailBtn,
    onEmailDialogCancel
  };

  return <ChangeStatusView {...viewProps} />;
};

export default ChangeStatusEngine;
